import React, { useRef, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowLeft, Calendar, ShoppingBag, ArrowRight, Play, Sparkles } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import { useMenuLabel } from '@/lib/menu-labels';
import ArtworkCard from '@/components/ArtworkCard';
import type { DropshopCollection } from '@/types';

/** Fade-in wrapper triggered on scroll */
const FadeIn: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className, delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const DropshopDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, localize } = useTranslation();
  const collections = useAdminStore((s) => s.dropshopCollections);
  const allArtworks = useAdminStore((s) => s.artworks);
  const menuLabel = useMenuLabel();

  const storeCollection = collections.find((c) => c.slug === slug);

  // Always fetch fresh data from API to ensure latest bio/artist fields (bypasses stale cache)
  const [freshCollection, setFreshCollection] = useState<DropshopCollection | null>(null);
  useEffect(() => {
    if (!slug) return;
    fetch(`/api/v1/dropshop/${slug}`)
      .then((r) => r.json())
      .then((d) => { if (d?.data) setFreshCollection(d.data as DropshopCollection); })
      .catch(() => {});
  }, [slug]);

  const collection = freshCollection || storeCollection;

  if (!collection) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <p className="text-black/30 dark:text-white/30">Collection not found</p>
      </div>
    );
  }

  const artistObj = typeof collection.artist !== 'string' ? collection.artist : null;
  const artistName = artistObj ? localize(artistObj.name) : (collection.artist as string);

  const relatedArtworks = collection.artworkIds
    .map((id) => allArtworks.find((a) => a._id === id))
    .filter(Boolean) as typeof allArtworks;

  // Separate gallery images (exclude cover which is shown in hero)
  const galleryImages = collection.images.filter((img) => img !== collection.coverImage);
  // Map gallery images to corresponding artworks
  const galleryWithArtwork = galleryImages.map((img, i) => ({
    src: img,
    artwork: i < relatedArtworks.length ? relatedArtworks[i] : null,
  }));

  const releaseDate = new Date(collection.releaseDate).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors duration-700">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">

        {/* Back button */}
        <button
          onClick={() => navigate('/dropshop')}
          className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors mb-8"
        >
          <ArrowLeft size={14} />
          {menuLabel('dropshop', t.dropshop.title)}
        </button>

        {/* ═══════ 1. HERO — Cover + Title side by side ═══════ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 mb-14"
        >
          {/* Cover image */}
          <div className="relative rounded-2xl overflow-hidden bg-[#f5f5f5] dark:bg-[#111]">
            <img
              src={collection.coverImage}
              alt={localize(collection.title)}
              className="w-full aspect-[4/5] object-cover"
            />
            <div className="absolute top-3 left-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                <span className="text-[8px] font-bold uppercase tracking-[0.25em] text-white/90">
                  {t.dropshop.limitedEdition}
                </span>
              </div>
            </div>
          </div>

          {/* Title + meta */}
          <div className="flex flex-col justify-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tight leading-[0.95] mb-4">
              {localize(collection.title)}
            </h1>

            <p className="text-sm text-black/40 dark:text-white/40 font-light mb-6">
              by{' '}
              {artistObj ? (
                <Link
                  to={`/artists/${artistObj.slug}`}
                  className="text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white underline underline-offset-4 transition-colors"
                >
                  {artistName}
                </Link>
              ) : artistName}
            </p>

            <div className="flex items-center gap-3 p-3 rounded-xl border border-black/5 dark:border-white/5 bg-[#fafafa] dark:bg-[#0a0a0a] w-fit mb-6">
              <Calendar size={14} className="text-black/30 dark:text-white/30" />
              <div>
                <p className="text-[8px] font-bold uppercase tracking-[0.2em] text-black/25 dark:text-white/25">
                  {t.dropshop.releaseDate}
                </p>
                <p className="text-xs font-medium">{releaseDate}</p>
              </div>
            </div>

            {/* Quick stats */}
            <div className="flex items-center gap-4 text-[9px] font-bold uppercase tracking-[0.15em] text-black/30 dark:text-white/30">
              <span className="flex items-center gap-1.5">
                <Sparkles size={11} />
                {collection.artworkIds.length} {t.dropshop.relatedArtworks}
              </span>
              <span className="flex items-center gap-1.5">
                {collection.images.length} Photos
              </span>
            </div>
          </div>
        </motion.div>

        {/* ═══════ 2. COLLECTION STORY — Text block ═══════ */}
        <FadeIn className="mb-14">
          <div className="max-w-3xl">
            <p className="text-[9px] font-bold uppercase tracking-[0.3em] text-black/25 dark:text-white/25 mb-4">
              {t.dropshop.collectionStory}
            </p>
            <p className="text-base sm:text-lg text-black/60 dark:text-white/60 leading-relaxed font-light whitespace-pre-line text-justify">
              {localize(collection.description)}
            </p>
          </div>
        </FadeIn>

        {/* ═══════ 3. GALLERY — Remaining images in 2-col grid ═══════ */}
        {galleryWithArtwork.length > 0 && (
          <FadeIn className="mb-14">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {galleryWithArtwork.map((item, i) => (
                <div key={i} className="relative group overflow-hidden rounded-xl bg-[#f5f5f5] dark:bg-[#111]">
                  <img
                    src={item.src}
                    alt={item.artwork ? localize(item.artwork.title) : localize(collection.title)}
                    className="w-full aspect-[4/5] object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  {item.artwork && (
                    <Link
                      to={`/artwork/${item.artwork.slug}`}
                      className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-2 bg-white text-black text-[8px] font-bold uppercase tracking-[0.15em] rounded-full shadow-lg opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 hover:bg-black hover:text-white"
                    >
                      <ShoppingBag size={11} />
                      {t.dropshop.buyThisPrint}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </FadeIn>
        )}

        {/* ═══════ 4. VIDEO ═══════ */}
        {collection.videoUrl && (
          <FadeIn className="mb-14">
            <div className="relative rounded-xl overflow-hidden bg-black">
              <div className="flex items-center gap-2 absolute top-3 left-3 z-10 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full">
                <Play size={10} fill="white" className="text-white" />
                <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-white/80">
                  {t.dropshop.watchVideo}
                </span>
              </div>
              <video
                src={collection.videoUrl}
                controls
                playsInline
                poster={collection.coverImage}
                className="w-full aspect-video object-cover"
              />
            </div>
          </FadeIn>
        )}

        {/* ═══════ 5. ARTIST SPOTLIGHT ═══════ */}
        {artistObj && (
          <FadeIn className="mb-14">
            <div className="flex flex-col sm:flex-row items-center gap-6 p-6 sm:p-8 rounded-2xl bg-black dark:bg-white text-white dark:text-black">
              <div className="shrink-0">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden ring-2 ring-white/10 dark:ring-black/10">
                  <img
                    src={artistObj.portrait}
                    alt={localize(artistObj.name)}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 text-center sm:text-left">
                <p className="text-[8px] font-bold uppercase tracking-[0.3em] text-white/30 dark:text-black/30 mb-1">
                  {t.dropshop.artistSpotlight}
                </p>
                <h3 className="text-lg sm:text-xl font-syncopate font-bold uppercase tracking-tight mb-2">
                  {localize(artistObj.name)}
                </h3>
                <p className="text-xs font-light text-white/50 dark:text-black/50 leading-relaxed mb-4 line-clamp-3">
                  {localize(artistObj.bio)}
                </p>
                <Link
                  to={`/artists/${artistObj.slug}`}
                  className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.2em] text-white/60 dark:text-black/60 hover:text-white dark:hover:text-black transition-colors"
                >
                  {t.dropshop.viewAllWorks}
                  <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </FadeIn>
        )}

        {/* ═══════ 6. AVAILABLE PRINTS ═══════ */}
        {relatedArtworks.length > 0 && (
          <FadeIn>
            <h2 className="text-lg sm:text-xl font-syncopate font-bold uppercase tracking-tight mb-6">
              {t.dropshop.availablePrints}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
              {relatedArtworks.map((artwork) => (
                <ArtworkCard key={artwork._id} artwork={artwork} />
              ))}
            </div>
          </FadeIn>
        )}
      </div>
    </div>
  );
};

export default DropshopDetailPage;
